import { Box, Button } from '@factorialco/deprecated-design-system'
import { sortBy } from 'lodash'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useCreate, useDraftGroups } from 'modules/expenses/hooks/useGroup'

import { GroupNameField } from 'modules/expenses/components/expenses/New/GroupModal'

interface AddToGroupModalProps {
  onSubmit: (groupId: number) => void
  onClose: () => void
}

interface AddToGroupFormValues {
  id: number | null
  name: string
  create: boolean
}

const AddToGroupModal = ({ onSubmit, onClose }: AddToGroupModalProps) => {
  const { groups, loading } = useDraftGroups()
  const { create } = useCreate()
  const { company } = useCurrentSession()

  const handleSubmit = (groupId: number) => {
    onClose()
    onSubmit(groupId)
  }

  const { values, ...form } = useForm<AddToGroupFormValues>({
    defaultValues: {
      id: null,
      create: false,
      name: '',
    },
    validate: (data) => {
      const errors: FormErrors<AddToGroupFormValues> = {}

      if (!data.id && !data.create) {
        errors.id = i18n.t('expenses_v2.list.actions.modal.add_to_group.errors.no_group_id')
      }

      if (!data.name.trim() && data.create) {
        errors.name = i18n.t('expenses_v2.list.actions.modal.add_to_group.errors.no_group_name')
      }

      return errors
    },
    onSubmit: async (data) => {
      if (!data.create && data.id) {
        handleSubmit(data.id)
        return { success: true, data }
      }

      const result = await create(data.name, company.id)
      const handled = handleResult(result, (formErrors) => {
        form.setErrors({ name: formErrors.name })
      })

      if (handled.success) {
        const group = handled.data.expensableGroup
        if (!group) return handled
        handleSubmit(group.id)
      }

      return handled
    },
  })

  const selectedGroup = groups.find(({ id }) => id === values.id)

  return (
    <Modal
      title={i18n.t('expenses_v2.list.actions.modal.add_to_group.title')}
      description={i18n.t('expenses_v2.list.actions.modal.add_to_group.description')}
      footer={
        <>
          <Button type="secondary" disabled={form.isSubmitting} onClick={onClose}>
            {i18n.t('expenses_v2.buttons.cancel')}
          </Button>
          <Button disabled={form.isSubmitting} submit>
            {i18n.t('expenses_v2.buttons.add_expenses')}
          </Button>
        </>
      }
      onSubmit={() => form.handleSubmit()}
      onClose={onClose}
      illustration="alert"
    >
      <Box gap="s24">
        <DropdownSearchBar
          name={
            selectedGroup?.name ??
            (values.create
              ? i18n.t('expenses_v2.list.actions.modal.add_to_group.create_option')
              : '')
          }
          data={sortBy(
            groups.map(({ id, name }) => ({ label: name, value: id })),
            ['label']
          )}
          label={i18n.t('expenses_v2.list.actions.modal.add_to_group.label')}
          labelNotFound={i18n.t('expenses_v2.list.actions.modal.add_to_group.no_groups')}
          placeholder={i18n.t('expenses_v2.list.actions.modal.add_to_group.placeholder')}
          selectLabel={i18n.t('expenses_v2.list.actions.modal.add_to_group.create_option')}
          searchLabel={i18n.t('common.search')}
          sortLabel={i18n.t('common.name')}
          loading={loading}
          error={form.touched.id ? form.errors.id : undefined}
          onSelect={({ value }) => form.setValues({ id: Number(value), create: false, name: '' })}
          onSelectEmpty={() => form.setValues({ ...values, id: null, create: true })}
          mandatory
        />

        {values.create && (
          <GroupNameField
            value={values.name}
            error={form.touched.name ? form.errors.name : undefined}
            onChange={(value) => form.setFieldValue('name', value)}
          />
        )}
      </Box>
    </Modal>
  )
}

export default AddToGroupModal
